import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Package, IndianRupee, ShoppingCart, Wallet, X, Trash2, Pencil, Truck, ExternalLink, Loader2, ShieldAlert, Tag, Eye, TrendingUp, Download, RefreshCw, ListChecks, Store } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { api, rupee } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MediaUploader from "@/components/MediaUploader";
import { toast } from "sonner";

const STATUS_NEXT = { Confirmed: "In Progress", "In Progress": "Shipped", Shipped: "Delivered" };

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "bg-clay text-sand border-clay" : "border-line text-ink/70 hover:border-clay"}`}>{children}</button>
);

const EMPTY = {
  title: "", description: "", price: "", discount: 0, images: [], video_url: "",
  delivery_days: 7, refundable: false, category: "", occasions: [], recipients: [], festivals: [],
  shipping: { mode: "default", local: 0, within_state: 0, rest_of_india: 0 },
};

export default function SellerDashboard() {
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [tax, setTax] = useState({ categories: [], occasions: [], recipients: [], festivals: [] });
  const [modal, setModal] = useState(null); // product form data or null
  const [saving, setSaving] = useState(false);
  const [shipFor, setShipFor] = useState(null);
  const [ship, setShip] = useState({ courier: "", tracking_number: "" });
  const [respondTo, setRespondTo] = useState(null);
  const [dResp, setDResp] = useState({ message: "", resolution: "refund" });
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState({ code: "", percent: 10, min_order: 0, start_date: "", end_date: "" });
  const [analytics, setAnalytics] = useState(null);
  const [shipping, setShipping] = useState(null);
  const [savingShip, setSavingShip] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importJob, setImportJob] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPrices, setBulkPrices] = useState({});
  const [savingBulk, setSavingBulk] = useState(false);
  const [profile, setProfile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (user) setProfile({
      name: user.name || "", specialty: user.specialty || "", bio: user.bio || "",
      phone: user.phone || "", shop_logo: user.shop_logo || "",
      pickup_address: { line1: "", city: "", state: "", pincode: "", ...(user.pickup_address || {}) },
    });
  }, [user]);

  const saveProfile = async () => {
    if (!profile.name.trim()) { toast.error("Shop name is required"); return; }
    setSavingProfile(true);
    try {
      await api.put("/seller/profile", profile);
      await refresh();
      toast.success("Shop profile saved — your public page is updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save profile");
    } finally { setSavingProfile(false); }
  };

  const load = useCallback(() => {
    api.get("/seller/dashboard").then(({ data }) => setStats(data));
    api.get("/seller/products").then(({ data }) => setProducts(data));
    api.get("/seller/orders").then(({ data }) => setOrders(data));
    api.get("/seller/disputes").then(({ data }) => setDisputes(data));
    api.get("/seller/coupons").then(({ data }) => setCoupons(data));
    api.get("/seller/analytics").then(({ data }) => setAnalytics(data));
    api.get("/seller/shipping").then(({ data }) => setShipping(data));
  }, []);

  useEffect(() => { api.get("/taxonomy").then(({ data }) => setTax(data)); load(); }, [load]);

  const openNew = () => setModal({ ...EMPTY });

  // resume polling if an import is already running when the dashboard mounts
  useEffect(() => {
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get("/seller/import-catalog/status");
        if (data.status === "none") return;
        setImportJob(data);
        if (data.status === "running" || data.status === "queued") {
          timer = setTimeout(poll, 2000);
        } else if (data.status === "done") {
          load();
        }
      } catch { /* ignore */ }
    };
    poll();
    return () => clearTimeout(timer);
  }, [load]);

  const startImport = async () => {
    if (!importUrl.trim()) { toast.error("Paste your store's website URL first"); return; }
    try {
      await api.post("/seller/import-catalog", { site_url: importUrl.trim() });
      setImportJob({ status: "queued", done: 0, total: 0 });
      toast.success("Import started — this can take a minute");
      pollImport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start import");
    }
  };

  const pollImport = () => {
    const tick = async () => {
      const { data } = await api.get("/seller/import-catalog/status");
      setImportJob(data);
      if (data.status === "running" || data.status === "queued") {
        setTimeout(tick, 2000);
      } else if (data.status === "done") {
        const label = data.mode === "sync"
          ? (data.imported ? `Synced ${data.imported} new products` : "You're up to date — no new products")
          : `Imported ${data.imported} products (${data.active} live, ${data.hidden} need a price)`;
        toast.success(label);
        load();
      } else if (data.status === "failed") {
        toast.error(data.error || "Import failed");
      }
    };
    setTimeout(tick, 2000);
  };

  const syncNow = async () => {
    try {
      await api.post("/seller/import-catalog/sync-now");
      setImportJob((j) => ({ ...(j || {}), status: "queued" }));
      toast.success("Checking your store for new products…");
      pollImport();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not sync");
    }
  };

  const toggleAutoSync = async (enabled) => {
    try {
      await api.post("/seller/auto-sync", { enabled });
      setImportJob((j) => ({ ...(j || {}), auto_sync: enabled }));
      toast.success(enabled ? "Auto-sync on — we'll check daily for new products" : "Auto-sync turned off");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update");
    }
  };

  const openBulkPrice = () => {
    const init = {};
    products.forEach((p) => { init[p.id] = p.price || ""; });
    setBulkPrices(init);
    setBulkOpen(true);
  };

  const saveBulkPrices = async () => {
    const items = Object.entries(bulkPrices)
      .filter(([id, v]) => v !== "" && v != null)
      .map(([id, v]) => ({ id, price: Number(v) }));
    if (!items.length) { toast.error("Enter at least one price"); return; }
    setSavingBulk(true);
    try {
      const { data } = await api.put("/seller/products/bulk-price", { items });
      toast.success(`Updated ${data.updated} products`);
      setBulkOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save prices");
    } finally { setSavingBulk(false); }
  };
  const openEdit = (p) => setModal({ ...p, images: p.images || [], video_url: p.video_url || "" });

  const toggleTag = (field, val) => setModal((m) => ({
    ...m, [field]: m[field].includes(val) ? m[field].filter((x) => x !== val) : [...m[field], val],
  }));

  const saveProduct = async () => {
    if (!modal.title || !modal.price || !modal.category || !modal.images.length) {
      toast.error("Title, price, category and at least one photo are required"); return;
    }
    setSaving(true);
    const sh = modal.shipping || { mode: "default" };
    const payload = {
      ...modal,
      price: Number(modal.price), discount: Number(modal.discount) || 0,
      delivery_days: Number(modal.delivery_days) || 7,
      images: modal.images,
      shipping: {
        mode: sh.mode || "default",
        local: Number(sh.local) || 0,
        within_state: Number(sh.within_state) || 0,
        rest_of_india: Number(sh.rest_of_india) || 0,
      },
    };
    delete payload.id; delete payload.seller_id; delete payload.seller_name;
    delete payload.rating; delete payload.review_count; delete payload.active; delete payload.created_at;
    try {
      if (modal.id) await api.put(`/products/${modal.id}`, payload);
      else await api.post("/products", payload);
      toast.success(modal.id ? "Product updated" : "Product listed!");
      setModal(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const removeProduct = async (id) => {
    await api.delete(`/products/${id}`);
    toast.success("Product removed"); load();
  };

  const advance = async (o, status) => {
    if (status === "Shipped") { setShipFor(o); return; }
    await api.post(`/seller/orders/${o.id}/status`, { status });
    toast.success(`Order marked ${status}`); load();
  };

  const confirmShip = async () => {
    if (!ship.courier || !ship.tracking_number) { toast.error("Enter courier & tracking number"); return; }
    await api.post(`/seller/orders/${shipFor.id}/status`, { status: "Shipped", ...ship });
    toast.success("Buyer notified — order shipped!");
    setShipFor(null); setShip({ courier: "", tracking_number: "" }); load();
  };

  const withdraw = async () => {
    try {
      const { data } = await api.post("/seller/withdraw", { amount: stats.wallet_balance });
      toast.success(`₹${stats.wallet_balance} sent to your bank`);
      setStats({ ...stats, wallet_balance: data.new_balance });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Withdrawal failed");
    }
  };

  const submitDispute = async () => {
    if (!dResp.message.trim()) { toast.error("Add a message for the buyer"); return; }
    try {
      await api.post(`/seller/disputes/${respondTo.id}/respond`, dResp);
      toast.success(dResp.resolution === "refund" ? "Refund approved & buyer notified" : "Response sent to buyer");
      setRespondTo(null); setDResp({ message: "", resolution: "refund" }); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not respond");
    }
  };

  const openDisputes = disputes.filter((d) => d.status === "Open").length;

  const saveCoupon = async () => {
    if (!couponForm.code.trim()) { toast.error("Enter a coupon code"); return; }
    try {
      await api.post("/seller/coupons", {
        code: couponForm.code, percent: Number(couponForm.percent),
        min_order: Number(couponForm.min_order) || 0, active: true,
        start_date: couponForm.start_date || null, end_date: couponForm.end_date || null,
      });
      toast.success("Coupon saved");
      setCouponForm({ code: "", percent: 10, min_order: 0, start_date: "", end_date: "" });
      api.get("/seller/coupons").then(({ data }) => setCoupons(data));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save coupon");
    }
  };

  const deleteCoupon = async (id) => {
    await api.delete(`/seller/coupons/${id}`);
    toast.success("Coupon deleted");
    setCoupons(coupons.filter((c) => c.id !== id));
  };

  const saveShipping = async () => {
    if (!shipping.origin_city?.trim() || !shipping.origin_state?.trim()) {
      toast.error("Enter your dispatch city and state — it decides buyer shipping zones"); return;
    }
    setSavingShip(true);
    try {
      await api.put("/seller/shipping", {
        origin_city: shipping.origin_city, origin_state: shipping.origin_state,
        local: Number(shipping.local) || 0, within_state: Number(shipping.within_state) || 0,
        rest_of_india: Number(shipping.rest_of_india) || 0, free_above: Number(shipping.free_above) || 0,
        shipping_cap: Number(shipping.shipping_cap) || 0,
        state_rates: (shipping.state_rates || [])
          .filter((r) => (r.state || "").trim())
          .map((r) => ({ state: r.state.trim(), charge: Number(r.charge) || 0 })),
      });
      toast.success("Shipping settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save shipping");
    } finally { setSavingShip(false); }
  };

  const addStateRate = () => setShipping((s) => ({ ...s, state_rates: [...(s.state_rates || []), { state: "", charge: 0 }] }));
  const updateStateRate = (i, k, v) => setShipping((s) => {
    const rows = [...(s.state_rates || [])]; rows[i] = { ...rows[i], [k]: v }; return { ...s, state_rates: rows };
  });
  const removeStateRate = (i) => setShipping((s) => ({ ...s, state_rates: (s.state_rates || []).filter((_, idx) => idx !== i) }));

  const STAT_CARDS = stats ? [
    { icon: Package, l: "Live products", v: stats.product_count },
    { icon: ShoppingCart, l: "Orders", v: stats.order_count },
    { icon: Truck, l: "To fulfil", v: stats.pending_orders },
    { icon: IndianRupee, l: "Total sales", v: rupee(stats.total_sales) },
  ] : [];

  return (
    <div className="pt-24 sm:pt-28 pb-24 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="overline text-clay mb-3">Seller dashboard</div>
            <h1 className="font-serif text-4xl sm:text-6xl tracking-tighter">{user.name}</h1>
            <p className="text-mute mt-1">{user.specialty}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to={`/seller/${user.id}`} className="inline-flex items-center gap-2 text-sm border border-line rounded-full px-4 py-2 hover:border-clay" data-testid="view-shop-btn">
              View my shop <ExternalLink className="w-4 h-4" />
            </Link>
            <button onClick={openBulkPrice} className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-sm hover:border-clay transition-colors" data-testid="bulk-price-btn">
              <ListChecks className="w-4 h-4" /> Bulk prices
            </button>
            <button onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-clay text-sand px-5 py-2.5 text-sm hover:bg-forest transition-colors" data-testid="add-product-btn">
              <Plus className="w-4 h-4" /> Add product
            </button>
          </div>
        </div>

        <div className="flex gap-2 mt-8 border-b border-line overflow-x-auto">
          {[["overview", "Overview"], ["analytics", "Analytics"], ["products", `Products (${products.length})`], ["orders", `Orders (${orders.length})`], ["disputes", `Disputes${openDisputes ? ` (${openDisputes})` : ""}`], ["coupons", "Coupons"], ["shipping", "Shipping"], ["profile", "Shop profile"], ["wallet", "Wallet"]].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} className={`px-4 py-3 text-sm border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === v ? "border-clay text-clay" : "border-transparent text-mute"}`} data-testid={`seller-tab-${v}`}>{l}</button>
          ))}
        </div>

        {/* OVERVIEW */}
        {tab === "overview" && stats && (
          <div className="mt-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {STAT_CARDS.map((s) => (
                <div key={s.l} className="bg-surface border border-line rounded-sm p-5">
                  <s.icon className="w-5 h-5 text-clay" />
                  <div className="font-serif text-3xl mt-3">{s.v}</div>
                  <div className="text-sm text-mute">{s.l}</div>
                </div>
              ))}
            </div>
            <div className="mt-6 bg-forest text-sand rounded-sm p-6 sm:p-8 flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="overline text-terra">Wallet balance</div>
                <div className="font-serif text-4xl mt-1">{rupee(stats.wallet_balance)}</div>
                <p className="text-sand/60 text-sm mt-1">After 8% platform commission. Withdraw to your verified bank.</p>
              </div>
              <button onClick={() => setTab("wallet")} className="rounded-full bg-clay px-6 py-3 text-sm hover:bg-terra transition-colors">Manage payouts</button>
            </div>
          </div>
        )}

        {/* ANALYTICS */}
        {tab === "analytics" && analytics && (
          <div className="mt-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: Eye, l: "Total views", v: analytics.total_views },
                { icon: ShoppingCart, l: "Orders", v: analytics.total_orders },
                { icon: IndianRupee, l: "Earnings", v: rupee(analytics.total_earnings) },
                { icon: TrendingUp, l: "Conversion", v: `${analytics.conversion}%` },
                { icon: Truck, l: "Shipping earned", v: rupee(analytics.shipping_earned || 0),
                  sub: analytics.free_shipped_orders ? `${analytics.free_shipped_orders} free-shipped` : "you keep 100%" },
              ].map((s) => (
                <div key={s.l} className="bg-surface border border-line rounded-sm p-5" data-testid={`analytics-tile-${s.l.toLowerCase().replace(/\s/g,'-')}`}>
                  <s.icon className="w-5 h-5 text-clay" />
                  <div className="font-serif text-3xl mt-3">{s.v}</div>
                  <div className="text-sm text-mute">{s.l}</div>
                  {s.sub && <div className="text-xs text-clay/70 mt-0.5">{s.sub}</div>}
                </div>
              ))}
            </div>

            <div className="mt-6 bg-surface border border-line rounded-sm p-5 sm:p-6">
              <div className="overline text-mute mb-4">Earnings — last 14 days</div>
              <div className="h-64" data-testid="earnings-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={analytics.daily} margin={{ left: -18, right: 8, top: 8 }}>
                    <defs>
                      <linearGradient id="clayGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#8C3D2B" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#8C3D2B" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5DCD0" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#5C5C5C" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#5C5C5C" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5DCD0", fontSize: 12 }} formatter={(v, n) => n === "earnings" ? [rupee(v), "Earnings"] : [v, "Orders"]} />
                    <Area type="monotone" dataKey="earnings" stroke="#8C3D2B" strokeWidth={2} fill="url(#clayGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {analytics.alerts && analytics.alerts.length > 0 && (
              <div className="mt-6 bg-terra/10 border border-terra/30 rounded-sm p-5 sm:p-6" data-testid="analytics-alerts">
                <div className="overline text-clay mb-4 flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> Needs your attention</div>
                <div className="space-y-3">
                  {analytics.alerts.map((a, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm">
                      <Eye className="w-4 h-4 text-clay mt-0.5 shrink-0" />
                      <div><span className="font-serif text-base">{a.title}</span><p className="text-mute mt-0.5">{a.message}</p></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 bg-surface border border-line rounded-sm p-5 sm:p-6">
              <div className="overline text-mute mb-4">Top products by views</div>
              {analytics.top_products.length === 0 ? (
                <p className="text-mute text-sm">No product views yet.</p>
              ) : (
                <div className="space-y-3">
                  {analytics.top_products.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-line pb-3 last:border-0">
                      <span className="font-serif text-base">{p.title}</span>
                      <span className="text-mute flex items-center gap-4">
                        <span className="flex items-center gap-1"><Eye className="w-4 h-4" /> {p.views}</span>
                        <span className="flex items-center gap-1"><ShoppingCart className="w-4 h-4" /> {p.orders}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* PRODUCTS */}
        {tab === "products" && (
          <div className="mt-8">
            {/* Import from your existing website */}
            <div className="mb-8 rounded-sm border border-line bg-gradient-to-br from-surface to-line/30 p-5 sm:p-6" data-testid="import-catalog-card">
              <div className="flex items-start gap-3">
                <Download className="w-5 h-5 text-clay mt-0.5 shrink-0" />
                <div className="flex-1">
                  <h3 className="font-serif text-xl">Import your whole catalogue</h3>
                  <p className="text-sm text-mute mt-1">Already sell on your own website? Paste its link and we&apos;ll pull in your products — photos, prices, descriptions and categories — automatically. <span className="text-ink/70">Works with Shopify, WooCommerce and Odoo stores.</span></p>
                  <div className="flex flex-col sm:flex-row gap-2 mt-4">
                    <input value={importUrl} onChange={(e) => setImportUrl(e.target.value)} placeholder="https://yourstore.com  (Shopify / WooCommerce / Odoo)"
                      disabled={importJob && (importJob.status === "running" || importJob.status === "queued")}
                      className="flex-1 bg-sand border border-line rounded-sm px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-clay disabled:opacity-60" data-testid="import-url-input" />
                    <button onClick={startImport}
                      disabled={importJob && (importJob.status === "running" || importJob.status === "queued")}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-clay text-sand px-6 py-2.5 text-sm hover:bg-forest transition-colors disabled:opacity-60 whitespace-nowrap" data-testid="import-start-btn">
                      {importJob && (importJob.status === "running" || importJob.status === "queued")
                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</>
                        : <><Download className="w-4 h-4" /> Import catalogue</>}
                    </button>
                  </div>
                  {importJob && importJob.status !== "none" && (
                    <div className="mt-3 text-sm" data-testid="import-status">
                      {importJob.status === "queued" && <span className="text-mute">Starting…</span>}
                      {importJob.status === "running" && <span className="text-clay">Importing {importJob.done} of {importJob.total || "…"} products…</span>}
                      {importJob.status === "done" && <span className="text-forest">✓ Imported {importJob.imported} products{importJob.mode === "sync" ? " (sync)" : ` — ${importJob.active} live, ${importJob.hidden} need a price`}.</span>}
                      {importJob.status === "failed" && <span className="text-destructive">{importJob.error || "Import failed"}</span>}
                    </div>
                  )}
                  {importJob && importJob.import_site && (importJob.status === "done" || importJob.status === "none") && (
                    <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-line pt-4" data-testid="import-sync-controls">
                      <button onClick={syncNow} className="inline-flex items-center gap-2 text-sm border border-line rounded-full px-4 py-2 hover:border-clay transition-colors" data-testid="sync-now-btn">
                        <RefreshCw className="w-4 h-4" /> Sync now
                      </button>
                      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="auto-sync-toggle">
                        <input type="checkbox" checked={!!importJob.auto_sync} onChange={(e) => toggleAutoSync(e.target.checked)} className="accent-clay w-4 h-4" data-testid="auto-sync-checkbox" />
                        Keep in sync — check daily for new products
                      </label>
                      {importJob.import_platform && <span className="text-xs text-mute capitalize">· {importJob.import_platform} store linked</span>}
                    </div>
                  )}
                </div>
              </div>
            </div>
            {products.length === 0 ? (
              <div className="py-16 text-center text-mute">No products yet. <button onClick={openNew} className="text-clay link-underline">Add your first</button></div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                {products.map((p) => (
                  <div key={p.id} className="group" data-testid={`seller-product-${p.id}`}>
                    <div className="aspect-[4/5] rounded-sm overflow-hidden bg-surface relative">
                      <img src={p.images[0]} alt={p.title} className="w-full h-full object-cover" />
                      {!p.active && (
                        <span className="absolute top-2 left-2 text-[11px] px-2 py-0.5 rounded-full bg-terra text-sand" data-testid={`hidden-badge-${p.id}`}>Hidden · add price</span>
                      )}
                      <div className="absolute inset-0 bg-forest/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button onClick={() => openEdit(p)} className="w-9 h-9 grid place-items-center rounded-full bg-sand text-ink" data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => removeProduct(p.id)} className="w-9 h-9 grid place-items-center rounded-full bg-sand text-destructive" data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <h3 className="font-serif text-base mt-2 leading-snug">{p.title}</h3>
                    <div className="text-sm text-mute">{p.price > 0 ? `${rupee(p.price)} · ${p.delivery_days}d` : "No price yet"}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ORDERS */}
        {tab === "orders" && (
          <div className="mt-8 space-y-4">
            {orders.length === 0 ? (
              <div className="py-16 text-center text-mute">No orders yet.</div>
            ) : orders.map((o) => (
              <div key={o.id} className="bg-surface border border-line rounded-sm p-5" data-testid={`seller-order-${o.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="overline text-mute">Order #{o.id.slice(-6)}</div>
                    <div className="text-sm mt-0.5">{o.buyer_name} · {new Date(o.created_at).toLocaleDateString("en-IN")}</div>
                  </div>
                  <span className="text-sm px-3 py-1 rounded-full bg-clay/10 text-clay">{o.status}</span>
                </div>
                <div className="flex flex-wrap gap-4 mt-3">
                  {o.items.map((it) => (
                    <div key={it.product_id} className="flex gap-2 items-center text-sm">
                      <img src={it.image} alt="" className="w-12 h-14 rounded-sm object-cover" />
                      <div><div className="leading-tight">{it.title}</div><div className="text-mute text-xs">Qty {it.qty}</div></div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-xs text-mute bg-sand rounded-sm px-3 py-2 border border-line">
                  Ship to: {o.address?.full_name}, {o.address?.line1}, {o.address?.city}, {o.address?.state} - {o.address?.pincode} · 📞 {o.address?.phone}
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-line">
                  <div className="text-sm">
                    <span className="text-mute">You earn </span><b>{rupee(o.seller_earning)}</b> <span className="text-mute text-xs">(after {rupee(o.commission)} commission{o.shipping ? `, incl. ${rupee(o.shipping)} shipping` : ""})</span>
                  </div>
                  {STATUS_NEXT[o.status] && (
                    <button onClick={() => advance(o, STATUS_NEXT[o.status])} className="rounded-full bg-clay text-sand px-5 py-2 text-sm hover:bg-forest transition-colors" data-testid={`advance-order-${o.id}`}>
                      Mark {STATUS_NEXT[o.status]}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* DISPUTES */}
        {tab === "disputes" && (
          <div className="mt-8 space-y-4">
            {disputes.length === 0 ? (
              <div className="py-16 text-center text-mute">No disputes — happy customers all round.</div>
            ) : disputes.map((d) => (
              <div key={d.id} className="bg-surface border border-line rounded-sm p-5" data-testid={`seller-dispute-${d.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="overline text-mute">Order #{d.order_short} · {d.buyer_name}</div>
                    <div className="text-sm mt-0.5 capitalize">{d.kind.replace(/_/g, " ")} · {d.reason}</div>
                  </div>
                  <span className={`text-sm px-3 py-1 rounded-full ${d.status === "Open" ? "bg-terra/15 text-terra" : d.status === "Refunded" ? "bg-forest/15 text-forest" : "bg-clay/10 text-clay"}`}>{d.status}</span>
                </div>
                <p className="text-sm text-mute mt-3 bg-sand rounded-sm px-4 py-3 border border-line">{d.details}</p>
                {d.seller_message && <p className="text-sm mt-3"><span className="overline text-mute">Your response:</span> {d.seller_message}</p>}
                {d.status === "Open" && (
                  <button onClick={() => setRespondTo(d)} className="mt-4 rounded-full bg-clay text-sand px-5 py-2 text-sm hover:bg-forest transition-colors" data-testid={`respond-dispute-${d.id}`}>Respond</button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* COUPONS */}
        {tab === "coupons" && (
          <div className="mt-8 max-w-2xl">
            <div className="bg-surface border border-line rounded-sm p-5 sm:p-6">
              <h2 className="font-serif text-2xl mb-1">Create a discount code</h2>
              <p className="text-mute text-sm mb-5">Buyers apply your code at checkout for a discount on your items — perfect for festivals.</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="overline text-mute block mb-1.5">Code</label>
                  <input value={couponForm.code} onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })} placeholder="DIWALI10"
                    className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="coupon-code-input" />
                </div>
                <div>
                  <label className="overline text-mute block mb-1.5">% off</label>
                  <input type="number" value={couponForm.percent} onChange={(e) => setCouponForm({ ...couponForm, percent: e.target.value })}
                    className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="coupon-percent-input" />
                </div>
                <div>
                  <label className="overline text-mute block mb-1.5">Min order ₹</label>
                  <input type="number" value={couponForm.min_order} onChange={(e) => setCouponForm({ ...couponForm, min_order: e.target.value })}
                    className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="coupon-minorder-input" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="overline text-mute block mb-1.5">Starts (optional)</label>
                  <input type="date" value={couponForm.start_date} onChange={(e) => setCouponForm({ ...couponForm, start_date: e.target.value })}
                    className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="coupon-start-input" />
                </div>
                <div>
                  <label className="overline text-mute block mb-1.5">Ends (optional)</label>
                  <input type="date" value={couponForm.end_date} onChange={(e) => setCouponForm({ ...couponForm, end_date: e.target.value })}
                    className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="coupon-end-input" />
                </div>
              </div>
              <button onClick={saveCoupon} className="mt-5 inline-flex items-center gap-2 rounded-full bg-clay text-sand px-6 py-2.5 text-sm hover:bg-forest transition-colors" data-testid="save-coupon-btn">
                <Tag className="w-4 h-4" /> Create code
              </button>
            </div>

            <div className="mt-6 space-y-3">
              {coupons.length === 0 ? (
                <p className="text-mute text-sm py-8 text-center">No coupons yet.</p>
              ) : coupons.map((c) => (
                <div key={c.id} className="flex items-center justify-between bg-surface border border-line rounded-sm px-5 py-4" data-testid={`coupon-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-base bg-clay/10 text-clay px-3 py-1 rounded-sm">{c.code}</span>
                    <span className="text-sm text-mute">
                      {c.percent}% off{c.min_order > 0 ? ` · min ${rupee(c.min_order)}` : ""}
                      {c.end_date ? ` · until ${c.end_date}` : ""}
                      {c.end_date && c.end_date < new Date().toISOString().slice(0, 10) ? " · expired" : ""}
                    </span>
                  </div>
                  <button onClick={() => deleteCoupon(c.id)} className="text-mute hover:text-destructive" data-testid={`delete-coupon-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SHIPPING */}
        {tab === "shipping" && shipping && (
          <div className="mt-8 max-w-2xl" data-testid="shipping-panel">
            <div className="bg-surface border border-line rounded-sm p-5 sm:p-6">
              <h2 className="font-serif text-2xl mb-1">Shipping charges</h2>
              <p className="text-mute text-sm mb-5">Set rates by delivery zone. Buyers see the right charge at checkout based on where they live. You keep 100% of shipping — no commission on it.</p>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="overline text-mute block mb-1.5">Dispatch city</label>
                  <input value={shipping.origin_city || ""} onChange={(e) => setShipping({ ...shipping, origin_city: e.target.value })} placeholder="e.g. Pune"
                    className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="ship-origin-city" />
                </div>
                <div>
                  <label className="overline text-mute block mb-1.5">Dispatch state</label>
                  <input value={shipping.origin_state || ""} onChange={(e) => setShipping({ ...shipping, origin_state: e.target.value })} placeholder="e.g. Maharashtra"
                    className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="ship-origin-state" />
                </div>
              </div>
              <p className="text-xs text-mute mt-2">This decides each buyer&apos;s zone: same city = Local, same state = Within state, elsewhere = Rest of India.</p>

              <div className="grid sm:grid-cols-3 gap-4 mt-5">
                {[["local", "Local (same city) ₹"], ["within_state", "Within state ₹"], ["rest_of_india", "Rest of India ₹"]].map(([k, l]) => (
                  <div key={k}>
                    <label className="overline text-mute block mb-1.5">{l}</label>
                    <input type="number" min="0" value={shipping[k]} onChange={(e) => setShipping({ ...shipping, [k]: e.target.value })}
                      className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid={`ship-${k}`} />
                  </div>
                ))}
              </div>

              <div className="mt-5">
                <label className="overline text-mute block mb-1.5">Free shipping above ₹ (0 = off)</label>
                <input type="number" min="0" value={shipping.free_above} onChange={(e) => setShipping({ ...shipping, free_above: e.target.value })}
                  className="w-full sm:w-64 bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="ship-free-above" />
                <p className="text-xs text-mute mt-1.5">When a buyer&apos;s order from your shop crosses this amount, shipping is free.</p>
              </div>

              <div className="mt-5">
                <label className="overline text-mute block mb-1.5">Cap shipping per order ₹ (0 = no cap)</label>
                <input type="number" min="0" value={shipping.shipping_cap ?? 0} onChange={(e) => setShipping({ ...shipping, shipping_cap: e.target.value })}
                  className="w-full sm:w-64 bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="ship-cap" />
                <p className="text-xs text-mute mt-1.5">Buyers ordering many items never pay more than this in shipping from your shop.</p>
              </div>

              <div className="mt-6 border-t border-line pt-5">
                <div className="flex items-center justify-between mb-1">
                  <label className="overline text-mute">State-specific rates (optional)</label>
                  <button onClick={addStateRate} className="text-xs text-clay link-underline inline-flex items-center gap-1" data-testid="add-state-rate-btn"><Plus className="w-3.5 h-3.5" /> Add state</button>
                </div>
                <p className="text-xs text-mute mb-3">Override the zone rate for specific states (e.g. far states cost more). Takes priority over the 3 zones above.</p>
                <div className="space-y-2">
                  {(shipping.state_rates || []).length === 0 && <p className="text-xs text-mute">No state overrides — the 3 zone rates apply everywhere.</p>}
                  {(shipping.state_rates || []).map((r, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`state-rate-row-${i}`}>
                      <input value={r.state} onChange={(e) => updateStateRate(i, "state", e.target.value)} placeholder="State e.g. Kerala"
                        className="flex-1 bg-sand border border-line rounded-sm px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid={`state-rate-name-${i}`} />
                      <input type="number" min="0" value={r.charge} onChange={(e) => updateStateRate(i, "charge", e.target.value)} placeholder="₹"
                        className="w-28 bg-sand border border-line rounded-sm px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid={`state-rate-charge-${i}`} />
                      <button onClick={() => removeStateRate(i)} className="text-mute hover:text-destructive" data-testid={`remove-state-rate-${i}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <button onClick={saveShipping} disabled={savingShip} className="mt-6 inline-flex items-center gap-2 rounded-full bg-clay text-sand px-6 py-2.5 text-sm hover:bg-forest transition-colors disabled:opacity-60" data-testid="save-shipping-btn">
                {savingShip ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Truck className="w-4 h-4" /> Save shipping settings</>}
              </button>
            </div>
            <p className="text-xs text-mute mt-4">Tip: you can override these rates for a specific product in its edit form (e.g. offer free shipping on one item).</p>
          </div>
        )}

        {/* SHOP PROFILE */}
        {tab === "profile" && profile && (
          <div className="mt-8 max-w-2xl" data-testid="profile-panel">
            <div className="bg-surface border border-line rounded-sm p-5 sm:p-6">
              <h2 className="font-serif text-2xl mb-1">Shop profile</h2>
              <p className="text-mute text-sm mb-5">This is what buyers see on your public shop page and the homepage feature.</p>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="overline text-mute block mb-1.5">Shop name *</label>
                  <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="profile-name" />
                </div>
                <div>
                  <label className="overline text-mute block mb-1.5">Specialty</label>
                  <input value={profile.specialty} onChange={(e) => setProfile({ ...profile, specialty: e.target.value })} placeholder="e.g. Resin art & keepsakes"
                    className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="profile-specialty" />
                </div>
              </div>

              <div className="mt-4">
                <label className="overline text-mute block mb-1.5">Your story / bio</label>
                <textarea value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} rows={3}
                  placeholder="Tell buyers who you are and how your pieces are made…"
                  className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay resize-none" data-testid="profile-bio" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="overline text-mute block mb-1.5">Contact phone</label>
                  <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="98765 43210"
                    className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="profile-phone" />
                </div>
                <div>
                  <label className="overline text-mute block mb-1.5">Shop logo</label>
                  <MediaUploader
                    images={profile.shop_logo ? [profile.shop_logo] : []}
                    video=""
                    onImages={(imgs) => setProfile({ ...profile, shop_logo: imgs[imgs.length - 1] || "" })}
                    onVideo={() => {}}
                  />
                </div>
              </div>

              <div className="mt-6 border-t border-line pt-5">
                <label className="overline text-mute block mb-1">Pickup address</label>
                <p className="text-xs text-mute mb-3">Where couriers collect your parcels. The city/state here should match your dispatch city on the Shipping tab.</p>
                <div className="space-y-3">
                  <input value={profile.pickup_address.line1} onChange={(e) => setProfile({ ...profile, pickup_address: { ...profile.pickup_address, line1: e.target.value } })} placeholder="Shop / street address"
                    className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="profile-pickup-line1" />
                  <div className="grid sm:grid-cols-3 gap-3">
                    <input value={profile.pickup_address.city} onChange={(e) => setProfile({ ...profile, pickup_address: { ...profile.pickup_address, city: e.target.value } })} placeholder="City"
                      className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="profile-pickup-city" />
                    <input value={profile.pickup_address.state} onChange={(e) => setProfile({ ...profile, pickup_address: { ...profile.pickup_address, state: e.target.value } })} placeholder="State"
                      className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="profile-pickup-state" />
                    <input value={profile.pickup_address.pincode} onChange={(e) => setProfile({ ...profile, pickup_address: { ...profile.pickup_address, pincode: e.target.value } })} placeholder="Pincode"
                      className="w-full bg-sand border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="profile-pickup-pincode" />
                  </div>
                </div>
              </div>

              <button onClick={saveProfile} disabled={savingProfile} className="mt-6 inline-flex items-center gap-2 rounded-full bg-clay text-sand px-6 py-2.5 text-sm hover:bg-forest transition-colors disabled:opacity-60" data-testid="save-profile-btn">
                {savingProfile ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Store className="w-4 h-4" /> Save shop profile</>}
              </button>
            </div>
          </div>
        )}

        {/* WALLET */}
        {tab === "wallet" && stats && (
          <div className="mt-8 max-w-lg">
            <div className="bg-forest text-sand rounded-sm p-8">
              <div className="overline text-terra">Available balance</div>
              <div className="font-serif text-5xl mt-2">{rupee(stats.wallet_balance)}</div>
              <p className="text-sand/60 text-sm mt-3">Payouts go to your verified account ending {user.bank_account?.slice(-4) || "----"}. Minimum withdrawal ₹500.</p>
              <button onClick={withdraw} disabled={stats.wallet_balance < 500}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-clay px-7 py-3 text-sm hover:bg-terra transition-colors disabled:opacity-50" data-testid="withdraw-btn">
                <Wallet className="w-4 h-4" /> Withdraw to bank
              </button>
            </div>
          </div>
        )}
      </div>

      {/* PRODUCT MODAL */}
      <AnimatePresence>
        {modal && (
          <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center">
            <div className="absolute inset-0 bg-ink/50" onClick={() => setModal(null)} />
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ opacity: 0 }}
              className="relative bg-sand w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-sm p-6 sm:p-8" data-testid="product-modal">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-3xl tracking-tight">{modal.id ? "Edit product" : "List a new product"}</h2>
                <button onClick={() => setModal(null)}><X className="w-6 h-6" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="overline text-mute block mb-1.5">Title *</label>
                  <input value={modal.title} onChange={(e) => setModal({ ...modal, title: e.target.value })} className="w-full bg-surface border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="pf-title" />
                </div>
                <div>
                  <label className="overline text-mute block mb-1.5">Description</label>
                  <textarea value={modal.description} onChange={(e) => setModal({ ...modal, description: e.target.value })} rows={3} className="w-full bg-surface border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay resize-none" data-testid="pf-desc" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="overline text-mute block mb-1.5">Price (₹) *</label>
                    <input type="number" value={modal.price} onChange={(e) => setModal({ ...modal, price: e.target.value })} className="w-full bg-surface border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="pf-price" />
                  </div>
                  <div>
                    <label className="overline text-mute block mb-1.5">Discount (%)</label>
                    <input type="number" value={modal.discount} onChange={(e) => setModal({ ...modal, discount: e.target.value })} className="w-full bg-surface border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="pf-discount" />
                  </div>
                </div>
                <div>
                  <label className="overline text-mute block mb-1.5">Photos & video *</label>
                  <MediaUploader
                    images={modal.images}
                    video={modal.video_url}
                    onImages={(imgs) => setModal({ ...modal, images: imgs })}
                    onVideo={(v) => setModal({ ...modal, video_url: v })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="overline text-mute block mb-1.5">Delivery days *</label>
                    <input type="number" value={modal.delivery_days} onChange={(e) => setModal({ ...modal, delivery_days: e.target.value })} className="w-full bg-surface border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="pf-delivery" />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm cursor-pointer pb-3">
                      <input type="checkbox" checked={modal.refundable} onChange={(e) => setModal({ ...modal, refundable: e.target.checked })} className="accent-clay w-4 h-4" data-testid="pf-refundable" />
                      Refundable
                    </label>
                  </div>
                </div>
                <div>
                  <label className="overline text-mute block mb-2">Category *</label>
                  <div className="space-y-2">
                    {tax.categories.map((t) => (
                      <div key={t.slug}>
                        {t.children && t.children.length > 0 ? (
                          <>
                            <div className="text-xs font-semibold text-ink/70 mb-1">{t.name}</div>
                            <div className="flex flex-wrap gap-2">
                              {t.children.map((c) => <Chip key={c.slug} active={modal.category === c.slug} onClick={() => setModal({ ...modal, category: c.slug })} testid={`pf-cat-${c.slug}`}>{c.name}</Chip>)}
                            </div>
                          </>
                        ) : (
                          <Chip active={modal.category === t.slug} onClick={() => setModal({ ...modal, category: t.slug })} testid={`pf-cat-${t.slug}`}>{t.name}</Chip>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="overline text-mute block mb-2">Recipients</label>
                  <div className="flex flex-wrap gap-2">{tax.recipients.map((c) => <Chip key={c} active={modal.recipients.includes(c)} onClick={() => toggleTag("recipients", c)}>{c}</Chip>)}</div>
                </div>
                <div>
                  <label className="overline text-mute block mb-2">Occasions</label>
                  <div className="flex flex-wrap gap-2">{tax.occasions.map((c) => <Chip key={c.slug} active={modal.occasions.includes(c.slug)} onClick={() => toggleTag("occasions", c.slug)}>{c.name}</Chip>)}</div>
                </div>
                <div>
                  <label className="overline text-mute block mb-2">Festivals</label>
                  <div className="flex flex-wrap gap-2">{tax.festivals.map((c) => <Chip key={c} active={modal.festivals.includes(c)} onClick={() => toggleTag("festivals", c)}>{c}</Chip>)}</div>
                </div>
                <div>
                  <label className="overline text-mute block mb-2">Shipping for this product</label>
                  <div className="flex flex-wrap gap-2">
                    {[["default", "Use shop rates"], ["free", "Free shipping"], ["custom", "Custom rates"]].map(([m, l]) => (
                      <Chip key={m} active={(modal.shipping?.mode || "default") === m}
                        onClick={() => setModal({ ...modal, shipping: { ...(modal.shipping || { local: 0, within_state: 0, rest_of_india: 0 }), mode: m } })}
                        testid={`pf-ship-${m}`}>{l}</Chip>
                    ))}
                  </div>
                  {(modal.shipping?.mode === "custom") && (
                    <div className="grid grid-cols-3 gap-3 mt-3">
                      {[["local", "Local ₹"], ["within_state", "Within state ₹"], ["rest_of_india", "Rest of India ₹"]].map(([k, l]) => (
                        <div key={k}>
                          <label className="text-xs text-mute block mb-1">{l}</label>
                          <input type="number" min="0" value={modal.shipping?.[k] ?? 0}
                            onChange={(e) => setModal({ ...modal, shipping: { ...modal.shipping, [k]: e.target.value } })}
                            className="w-full bg-surface border border-line rounded-sm px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid={`pf-ship-${k}`} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button onClick={saveProduct} disabled={saving} className="w-full rounded-full bg-clay text-sand py-3.5 mt-6 text-sm hover:bg-forest transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-testid="pf-save">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : (modal.id ? "Save changes" : "List product")}
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* BULK PRICE MODAL */}
      <AnimatePresence>
        {bulkOpen && (
          <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center">
            <div className="absolute inset-0 bg-ink/50" onClick={() => setBulkOpen(false)} />
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ opacity: 0 }}
              className="relative bg-sand w-full sm:max-w-2xl max-h-[90vh] overflow-hidden rounded-t-2xl sm:rounded-sm flex flex-col" data-testid="bulk-price-modal">
              <div className="flex items-center justify-between p-6 border-b border-line">
                <div>
                  <h2 className="font-serif text-2xl tracking-tight">Set prices in bulk</h2>
                  <p className="text-mute text-sm mt-0.5">Type a price for each item. Items priced above ₹0 go live instantly.</p>
                </div>
                <button onClick={() => setBulkOpen(false)} data-testid="bulk-close"><X className="w-6 h-6" /></button>
              </div>
              <div className="overflow-y-auto p-6 space-y-2">
                {products.map((p) => (
                  <div key={p.id} className="flex items-center gap-3" data-testid={`bulk-row-${p.id}`}>
                    <img src={p.images[0]} alt="" className="w-12 h-14 rounded-sm object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm leading-tight truncate">{p.title}</div>
                      {!p.active && <span className="text-[11px] text-terra">Hidden · add price</span>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-mute text-sm">₹</span>
                      <input type="number" min="0" value={bulkPrices[p.id] ?? ""} onChange={(e) => setBulkPrices({ ...bulkPrices, [p.id]: e.target.value })}
                        className="w-24 bg-surface border border-line rounded-sm px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid={`bulk-input-${p.id}`} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-6 border-t border-line">
                <button onClick={saveBulkPrices} disabled={savingBulk} className="w-full rounded-full bg-clay text-sand py-3.5 text-sm hover:bg-forest transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-testid="bulk-save">
                  {savingBulk ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><ListChecks className="w-4 h-4" /> Save all prices</>}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* SHIP MODAL */}
      <AnimatePresence>
        {shipFor && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center">
            <div className="absolute inset-0 bg-ink/50" onClick={() => setShipFor(null)} />
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }}
              className="relative bg-sand w-full max-w-md mx-5 rounded-sm p-6 sm:p-8" data-testid="ship-modal">
              <h2 className="font-serif text-2xl mb-1">Ship order #{shipFor.id.slice(-6)}</h2>
              <p className="text-mute text-sm mb-5">Enter courier details. The buyer will be notified instantly.</p>
              <div className="space-y-4">
                <div>
                  <label className="overline text-mute block mb-1.5">Courier partner</label>
                  <input value={ship.courier} onChange={(e) => setShip({ ...ship, courier: e.target.value })} placeholder="Delhivery, Shiprocket…" className="w-full bg-surface border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="ship-courier" />
                </div>
                <div>
                  <label className="overline text-mute block mb-1.5">Tracking / AWB number</label>
                  <input value={ship.tracking_number} onChange={(e) => setShip({ ...ship, tracking_number: e.target.value })} className="w-full bg-surface border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay" data-testid="ship-tracking" />
                </div>
              </div>
              <button onClick={confirmShip} className="w-full rounded-full bg-clay text-sand py-3.5 mt-6 text-sm hover:bg-forest transition-colors" data-testid="ship-confirm">Mark as shipped & notify buyer</button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* DISPUTE RESPONSE MODAL */}
      <AnimatePresence>
        {respondTo && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center">
            <div className="absolute inset-0 bg-ink/50" onClick={() => setRespondTo(null)} />
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ opacity: 0 }}
              className="relative bg-sand w-full max-w-md mx-5 rounded-sm p-6 sm:p-8" data-testid="dispute-modal">
              <h2 className="font-serif text-2xl mb-1">Respond to {respondTo.buyer_name}</h2>
              <p className="text-mute text-sm mb-5">Order #{respondTo.order_short} · {respondTo.reason}</p>
              <div className="flex gap-2 mb-4">
                {[["refund", "Approve refund"], ["reject", "Decline"]].map(([v, l]) => (
                  <button key={v} type="button" onClick={() => setDResp({ ...dResp, resolution: v })}
                    className={`flex-1 rounded-full py-2.5 text-sm border transition-colors ${dResp.resolution === v ? (v === "refund" ? "bg-forest text-sand border-forest" : "bg-clay text-sand border-clay") : "border-line text-ink/70"}`}
                    data-testid={`resolution-${v}`}>{l}</button>
                ))}
              </div>
              {dResp.resolution === "refund" && (
                <p className="text-xs text-mute mb-3">Approving refunds {rupee(respondTo.amount)} to the buyer and reverses your earning for this order.</p>
              )}
              <textarea value={dResp.message} onChange={(e) => setDResp({ ...dResp, message: e.target.value })} rows={3} placeholder="Message to the buyer…"
                className="w-full bg-surface border border-line rounded-sm px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-clay resize-none" data-testid="dispute-message" />
              <button onClick={submitDispute} className="w-full rounded-full bg-clay text-sand py-3.5 mt-4 text-sm hover:bg-forest transition-colors" data-testid="dispute-submit">Send response</button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
